import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  PiFileTextLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiClockLight,
  PiDownloadSimpleLight,
  PiSpinnerGapLight,
  PiArrowsClockwiseLight,
  PiMagnifyingGlassLight,
  PiSparkleLight,
  PiListBulletsLight,
  PiBookmarkSimpleLight,
  PiArrowSquareOutLight,
  PiArrowFatUpLight,
  PiMinusLight,
  PiArrowFatDownLight,
  PiLockSimpleLight,
} from 'react-icons/pi';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';
import apis from '../../../../utils/apis';
import { useAppDispatch } from '../../../../store';
import { fetchStructureTemplates } from '../../../../store/actions/TemplateActions';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import { useLicenseGate } from '../../../../hooks/useLicenseGate';
import Badge from '../../../shared/TailwindComponents/Badge';
import { BadgeColor } from '../../../shared/TailwindComponents/types/badgeTypes';
import Button from '../../../shared/TailwindComponents/Button';
import SubTabBar from '../../../shared/TailwindComponents/SubTabBar';
import FilterBarWithTabs from '../../../shared/TailwindComponents/FilterBarWithTabs';
import FindMoreProgress from '../../FindMoreProgress';
import { GenerateSuggestionsProgress } from '../../../../hooks/useGenerateSuggestions';
import SkeletonClientItem from '../../../Clients/SkeletonClientItem';
import { formatDate, formatDateTime, ensureUTCString } from '../../../../utils/functions';
import MiniLoadingToCheckmark from '../../../shared/animations/MiniLoadingToCheckmark';
import {
  MergedDraftItem,
  formatCategoryName,
  getStatusColor,
  getStatusLabel,
} from './utils/draftUtils';

interface StructureTemplate {
  id: string;
  TemplateName: string;
  DraftType: string;
  OwnerType: string;
  Version: number;
  CreatedAt: string;
  UpdatedAt: string;
  status?: string;
  Owner?: {
    UserID: string;
    Name: string;
    Email: string;
    ProfilePicture: string | null;
    Initials: string;
  };
  Structure?: any;
}

interface AiDraftSuggestion {
  document_type_id: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

interface UnifiedDraftsViewProps {
  matterUuid: string | undefined;
  generatingDraftId: string | null;
  generatingDraftIds?: Set<string>;
  onGenerateDraft: (draftTypeId: string, structureTemplateId?: string) => void;
  onViewDraft: (draftTypeId: string, draftName: string, versionId?: string) => void;
  onRefresh: () => void;
  onRefreshReady?: (refreshFn: () => Promise<void>) => void;
  processingMessage?: string;
  processingProgress?: number;
  suggestedCategories?: string[];
  aiSuggestions?: AiDraftSuggestion[] | null;
  suggestionsSource?: 'ai' | 'rule_based' | null;
  isGeneratingSuggestions?: boolean;
  suggestionsProgress?: GenerateSuggestionsProgress;
  onGenerateSuggestions?: () => void;
  hasDocuments?: boolean;
  aiFeatureLocked?: boolean;
}

type ViewMode = 'table' | 'grid';

const UnifiedDraftsView: React.FC<UnifiedDraftsViewProps> = ({
  matterUuid,
  generatingDraftId,
  generatingDraftIds,
  onGenerateDraft,
  onViewDraft,
  onRefresh,
  onRefreshReady,
  processingMessage,
  processingProgress,
  suggestedCategories,
  aiSuggestions,
  suggestionsSource,
  isGeneratingSuggestions,
  suggestionsProgress,
  onGenerateSuggestions,
  hasDocuments = false,
  aiFeatureLocked = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();
  const hasTemplatesAccess = hasSectionAccess('templates');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [draftSubTab, setDraftSubTab] = useState<'suggested' | 'all' | 'templates'>('suggested');
  const [mergedDrafts, setMergedDrafts] = useState<MergedDraftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [structureTemplates, setStructureTemplates] = useState<StructureTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const onRefreshReadyRef = useRef(onRefreshReady);
  onRefreshReadyRef.current = onRefreshReady;
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const storageKey = 'drafts-table-column-widths';
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse stored column widths:', e);
      }
    }
    return {
      'Name': 250,
      'Priority': 90,
      'Category': 130,
      'Status': 100,
      'Generated': 130,
      'Versions': 80,
      'Actions': 160,
    };
  });

  const [isResizing, setIsResizing] = useState(false);
  const resizingColRef = useRef<string | null>(null);
  const startXRef = useRef(0);

  const fetchMergedDrafts = useCallback(async () => {
    if (!matterUuid) return;

    try {
      setLoading(true);
      const response = await apis.get(
        `/drafts/list/${matterUuid}/merged`
      );

      if (response.status === 200) {
        setMergedDrafts(response.data.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch merged drafts:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to load drafts'),
          modalType: 'error',
          duration: 5000
        })
      );
    } finally {
      setLoading(false);
    }
  }, [matterUuid, dispatch]);

  // Combined initial load — fetch drafts + templates in parallel, show skeleton until both resolve
  useEffect(() => {
    if (!matterUuid) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const loadAll = async () => {
      setLoading(true);
      try {
        const [draftsRes, templatesRes] = await Promise.all([
          apis.get(`/drafts/list/${matterUuid}/merged`).catch((err) => {
            console.error('Failed to fetch merged drafts:', err);
            return null;
          }),
          hasTemplatesAccess
            ? dispatch(fetchStructureTemplates({ page: 1, limit: 100 })).catch((err: any) => {
                console.error('Failed to fetch structure templates:', err);
                return null;
              })
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        if (draftsRes && draftsRes.status === 200) {
          setMergedDrafts(draftsRes.data.documents || []);
        }
        if ((templatesRes as any)?.templates) {
          setStructureTemplates(
            (templatesRes as any).templates.filter((t: any) => t.status === 'completed' || !t.status)
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();

    // Expose a stable refresh function to the parent
    if (onRefreshReadyRef.current) {
      onRefreshReadyRef.current(fetchMergedDrafts);
    }

    return () => { cancelled = true; };
  }, [matterUuid, dispatch, fetchMergedDrafts, hasTemplatesAccess]);

  // Build AI suggestion lookup map
  const aiSuggestionMap = useMemo(() => {
    const map = new Map<string, AiDraftSuggestion>();
    if (aiSuggestions) {
      aiSuggestions.forEach((s) => map.set(s.document_type_id, s));
    }
    return map;
  }, [aiSuggestions]);

  // Template-generated drafts (generated from structure templates)
  const templateGeneratedDrafts = useMemo(() => {
    return mergedDrafts.filter((d) => (d as any).isTemplateGenerated);
  }, [mergedDrafts]);

  // Map structure template IDs to their generated drafts
  // document_type_id format: "template_{uuid}" → extract "{uuid}" to match template.id
  const templateDraftMap = useMemo(() => {
    const map = new Map<string, MergedDraftItem>();
    templateGeneratedDrafts.forEach((draft) => {
      const templateId = draft.document_type_id.replace(/^template_/, '');
      map.set(templateId, draft);
    });
    return map;
  }, [templateGeneratedDrafts]);

  const filteredDrafts = useMemo(() => {
    // Filter out template-generated items — they belong in the My Templates tab
    let drafts = mergedDrafts.filter((d) => !(d as any).isTemplateGenerated);

    // Sub-tab filtering
    if (draftSubTab === 'suggested') {
      if (aiSuggestions && aiSuggestions.length > 0) {
        // AI-powered suggestions: filter to AI-suggested draft types + already generated
        const aiTypeIds = new Set(aiSuggestions.map(s => s.document_type_id));
        drafts = drafts.filter((draft) =>
          draft.status !== 'not_generated' || aiTypeIds.has(draft.document_type_id)
        );
        // Sort by AI priority: high > medium > low, then already generated first
        const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
        drafts = [...drafts].sort((a, b) => {
          // Already generated drafts come first
          const aGenerated = a.status !== 'not_generated' ? -1 : 0;
          const bGenerated = b.status !== 'not_generated' ? -1 : 0;
          if (aGenerated !== bGenerated) return aGenerated - bGenerated;
          // Then sort by AI priority
          const aPriority = priorityOrder[aiSuggestionMap.get(a.document_type_id)?.priority || 'low'] ?? 3;
          const bPriority = priorityOrder[aiSuggestionMap.get(b.document_type_id)?.priority || 'low'] ?? 3;
          return aPriority - bPriority;
        });
      } else {
        // No AI suggestions generated yet — show empty state
        drafts = [];
      }
    } else if (draftSubTab === 'templates') {
      // Templates tab is handled separately — return empty for draft filtering
      return [];
    }
    // 'all' shows everything

    // Search filtering
    return drafts.filter((draft) =>
      draft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      draft.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [mergedDrafts, searchTerm, draftSubTab, aiSuggestions, aiSuggestionMap]);

  const draftSubTabs = useMemo(() => {
    // Exclude template-generated items from suggested/all counts
    const nonTemplateDrafts = mergedDrafts.filter((d) => !(d as any).isTemplateGenerated);

    let suggestedCount: number;
    if (aiSuggestions && aiSuggestions.length > 0) {
      const aiTypeIds = new Set(aiSuggestions.map(s => s.document_type_id));
      suggestedCount = nonTemplateDrafts.filter(d =>
        d.status !== 'not_generated' || aiTypeIds.has(d.document_type_id)
      ).length;
    } else {
      // No AI suggestions generated yet — count is 0
      suggestedCount = 0;
    }

    // My Templates count: structure templates + any generated drafts from templates not tied to a structure template
    const unmatchedGeneratedCount = templateGeneratedDrafts.filter(
      (d) => !structureTemplates.some((t) => templateDraftMap.get(t.id)?.document_type_id === d.document_type_id)
    ).length;
    const templatesCount = structureTemplates.length + unmatchedGeneratedCount;

    return [
      {
        id: 'suggested' as const,
        label: t('Suggestions'),
        icon: <PiSparkleLight className="h-3 w-3" />,
        count: suggestedCount,
        color: 'yellow' as BadgeColor,
      },
      {
        id: 'all' as const,
        label: t('All'),
        icon: <PiListBulletsLight className="h-3 w-3" />,
        count: nonTemplateDrafts.length,
        color: 'gray' as BadgeColor,
      },
      ...(hasTemplatesAccess ? [{
        id: 'templates' as const,
        label: t('My Templates'),
        icon: <PiBookmarkSimpleLight className="h-3 w-3" />,
        count: templatesCount,
        color: 'purple' as BadgeColor,
      }] : []),
    ];
  }, [mergedDrafts, t, aiSuggestions, structureTemplates, templateGeneratedDrafts, templateDraftMap, hasTemplatesAccess]);

  const handleDownloadDraft = async (draftTypeId: string, draftName: string) => {
    if (!matterUuid) return;

    setDownloading(draftTypeId);
    try {
      const response = await apis.get(`/drafts/${matterUuid}/${draftTypeId}`);

      if (response.status === 200) {
        const { content } = response.data;
        const element = document.createElement('a');
        const file = new Blob([content], { type: 'text/markdown' });
        element.href = URL.createObjectURL(file);
        element.download = `${draftName.replace(/\s+/g, '-')}.md`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Draft downloaded successfully'),
            modalType: 'success',
            duration: 3000
          })
        );
      }
    } catch (error) {
      console.error('Failed to download draft:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to download draft'),
          modalType: 'error',
          duration: 5000
        })
      );
    } finally {
      setDownloading(null);
    }
  };

  const toggleVersions = (draftTypeId: string) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(draftTypeId)) {
      newExpanded.delete(draftTypeId);
    } else {
      newExpanded.add(draftTypeId);
    }
    setExpandedVersions(newExpanded);
  };

  const formatDateAndTime = (dateStr: string): { date: string; time: string } => {
    const date = new Date(ensureUTCString(dateStr));
    const formattedDate = formatDate(dateStr);
    const time = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return { date: formattedDate, time };
  };

  const renderStatusBadge = (status: string) => {
    const color = getStatusColor(status);
    const label = getStatusLabel(status, t);
    let icon;

    if (status === 'completed') {
      icon = <PiCheckCircleLight className="h-3 w-3" />;
    } else if (status === 'processing') {
      icon = <MiniLoadingToCheckmark isLoading={true} spinnerSize={12} checkSize={12} />;
    } else if (status === 'failed') {
      icon = <PiWarningCircleLight className="h-3 w-3" />;
    }

    return (
      <Badge color={color} icon={icon}>
        {label}
      </Badge>
    );
  };

  const saveColumnWidths = (widths: Record<string, number>) => {
    const storageKey = 'drafts-table-column-widths';
    localStorage.setItem(storageKey, JSON.stringify(widths));
  };

  const handleResizeStart = (e: React.MouseEvent, column: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizingColRef.current = column;
    startXRef.current = e.clientX;
  };

  useEffect(() => {
    if (!isResizing || !resizingColRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      const currentCol = resizingColRef.current;

      if (!currentCol) return;

      setColumnWidths((prev) => {
        const newWidths = { ...prev };
        const currentWidth = newWidths[currentCol] || 0;
        const minWidth = 80;
        const newWidth = Math.max(minWidth, currentWidth + delta);

        newWidths[currentCol] = newWidth;
        return newWidths;
      });

      startXRef.current = e.clientX;
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizingColRef.current = null;

      setColumnWidths((prev) => {
        saveColumnWidths(prev);
        return prev;
      });
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const renderActionButtons = (draft: MergedDraftItem) => {
    const isGenerating = generatingDraftIds?.has(draft.document_type_id) || generatingDraftId === draft.document_type_id;

    if (isGenerating || draft.status === 'processing') {
      return (
        <div className="flex items-center gap-2 max-w-[220px]">
          <MiniLoadingToCheckmark isLoading={true} spinnerSize={16} checkSize={16} />
          <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
            {isGenerating && processingMessage ? processingMessage : t('Processing')}
          </span>
        </div>
      );
    }

    if (draft.status === 'completed') {
      return (
        <Button
          variant="primary"
          size="small"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewDraft(draft.document_type_id, draft.name); }}
        >
          {t('View')}
        </Button>
      );
    }

    if (draft.status === 'failed') {
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="small"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onGenerateDraft(draft.document_type_id); }}
            disabled={!hasDocuments || aiFeatureLocked}
            tooltip={aiFeatureLocked ? t('Upgrade your plan to use AI Drafts.') : !hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
            icon={aiFeatureLocked ? <PiLockSimpleLight className="h-3.5 w-3.5" /> : undefined}
          >
            {t('Retry')}
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewDraft(draft.document_type_id, draft.name); }}
          >
            {t('View')}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="small"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onGenerateDraft(draft.document_type_id); }}
          disabled={!hasDocuments || aiFeatureLocked}
          tooltip={aiFeatureLocked ? t('Upgrade your plan to use AI Drafts.') : !hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
          icon={aiFeatureLocked ? <PiLockSimpleLight className="h-3.5 w-3.5" /> : undefined}
        >
          {t('Generate')}
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewDraft(draft.document_type_id, draft.name); }}
        >
          {t('View')}
        </Button>
      </div>
    );
  };

  if (loading && filteredDrafts.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-light dark:shadow-dark">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <FilterBarWithTabs
            onSearchChange={setSearchTerm}
            viewMode={viewMode}
            setViewMode={setViewMode}
            noParentPadding={true}
            noParentHeight={true}
            onRefresh={onRefresh}
            isRefreshLoading={loading}
            itemCount={0}
            totalItemCount={0}
            itemCountLabel={t('drafts')}
          />
        </div>
        <div className="p-6">
          <SkeletonClientItem viewMode={viewMode} count={viewMode === 'grid' ? 3 : 2} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded shadow-light dark:shadow-dark">
      {/* Sub-tab navigation */}
      <div data-tour-id="drafts-sub-tabs">
      <SubTabBar
        tabs={draftSubTabs}
        activeTab={draftSubTab}
        onTabChange={(id) => setDraftSubTab(id as any)}
        className="px-4"
      />
      </div>
      <div data-tour-id="drafts-actions" className="p-4 border-b border-gray-200 dark:border-gray-700">
        <FilterBarWithTabs
          onSearchChange={setSearchTerm}
          viewMode={viewMode}
          setViewMode={setViewMode}
          noParentPadding={true}
          noParentHeight={true}
          onRefresh={onRefresh}
          isRefreshLoading={loading}
          additionalControls={
            draftSubTab === 'suggested' && onGenerateSuggestions ? (
              <Button
                size="small"
                variant="primary"
                onClick={onGenerateSuggestions}
                disabled={isGeneratingSuggestions || !hasDocuments || aiFeatureLocked}
                tooltip={aiFeatureLocked ? t('Upgrade your plan to use AI Drafts.') : !hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={aiFeatureLocked
                  ? <PiLockSimpleLight className="h-3.5 w-3.5" />
                  : isGeneratingSuggestions
                    ? <PiSpinnerGapLight className="h-3.5 w-3.5 animate-spin" />
                    : <PiSparkleLight className="h-3.5 w-3.5" />
                }
              >
                {isGeneratingSuggestions ? t('Generating...') : t('Generate Suggestions')}
              </Button>
            ) : undefined
          }
        />
      </div>

      {/* Suggestion generation progress overlay */}
      {draftSubTab === 'suggested' && suggestionsProgress && (isGeneratingSuggestions || suggestionsProgress.status === 'complete' || suggestionsProgress.status === 'failed') && (
        <FindMoreProgress
          progress={{ message: suggestionsProgress.message, percent: suggestionsProgress.percent, status: suggestionsProgress.status }}
          isGenerating={!!isGeneratingSuggestions}
        />
      )}

      {/* My Templates tab — shows generated template drafts + structure templates */}
      {draftSubTab === 'templates' && (() => {
        // Build unified list: templates with generated drafts at top, then unused templates
        const filteredTemplates = structureTemplates.filter(tmpl =>
          !searchTerm ||
          tmpl.TemplateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tmpl.DraftType?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Templates that have a generated draft (show at top with status)
        const templatesWithDrafts = filteredTemplates
          .filter((tmpl) => templateDraftMap.has(tmpl.id))
          .map((tmpl) => ({ template: tmpl, generatedDraft: templateDraftMap.get(tmpl.id)! }));

        // Templates without a generated draft
        const templatesWithoutDrafts = filteredTemplates
          .filter((tmpl) => !templateDraftMap.has(tmpl.id));

        // Generated drafts not tied to any known structure template (orphaned)
        const orphanedGeneratedDrafts = templateGeneratedDrafts.filter((d) => {
          const templateId = d.document_type_id.replace(/^template_/, '');
          return !structureTemplates.some((t) => t.id === templateId);
        }).filter((d) =>
          !searchTerm || d.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const hasAnyContent = templatesWithDrafts.length > 0 || templatesWithoutDrafts.length > 0 || orphanedGeneratedDrafts.length > 0;

        return (
          <>
            {templatesLoading && (
              <div className="p-6">
                <SkeletonClientItem viewMode="table" count={3} />
              </div>
            )}
            {!templatesLoading && !hasAnyContent && structureTemplates.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <PiBookmarkSimpleLight className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  {t('No templates yet')}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 max-w-xs mb-4">
                  {t('Create structure templates to use as starting points for your drafts.')}
                </p>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => history.push('/templates/add?type=structure')}
                  icon={<PiArrowSquareOutLight className="h-3.5 w-3.5" />}
                >
                  {t('Create Template')}
                </Button>
              </div>
            )}
            {!templatesLoading && hasAnyContent && (
              <div className="overflow-x-auto max-h-[calc(100vh-220px)] custom-scrollbar-main">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">{t('Name')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">{t('Document Type')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">{t('Status')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">{t('Generated')}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {/* Generated template drafts at the top */}
                    {templatesWithDrafts.map(({ template: tmpl, generatedDraft }) => {
                      const isGenerating = generatingDraftIds?.has(generatedDraft.document_type_id) || generatingDraftId === generatedDraft.document_type_id;
                      const isProcessing = isGenerating || generatedDraft.status === 'processing';
                      const { date, time } = (generatedDraft as any).generated_at
                        ? formatDateAndTime((generatedDraft as any).generated_at)
                        : { date: '', time: '' };

                      return (
                        <tr
                          key={tmpl.id}
                          className={clsx(
                            'transition-colors cursor-pointer',
                            isProcessing
                              ? 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                              : generatedDraft.status === 'completed'
                              ? 'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                          )}
                          onClick={() => onViewDraft(generatedDraft.document_type_id, generatedDraft.name)}
                        >
                          <td className="px-4 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {tmpl.TemplateName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {generatedDraft.name}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <Badge color="gray">{tmpl.DraftType || '-'}</Badge>
                          </td>
                          <td className="px-4 py-4 text-center">
                            {isProcessing ? (
                              <Badge color="blue" icon={<PiSpinnerGapLight className="h-3 w-3 animate-spin" />}>
                                {t('Processing')}
                              </Badge>
                            ) : generatedDraft.status === 'completed' ? (
                              <Badge color="green" icon={<PiCheckCircleLight className="h-3 w-3" />}>
                                {t('Completed')}
                              </Badge>
                            ) : generatedDraft.status === 'failed' ? (
                              <Badge color="red" icon={<PiWarningCircleLight className="h-3 w-3" />}>
                                {t('Failed')}
                              </Badge>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {(generatedDraft as any).generated_at ? (
                              <div>
                                <span className="text-sm text-gray-900 dark:text-white">{date}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{time}</span>
                              </div>
                            ) : isProcessing ? (
                              <MiniLoadingToCheckmark isLoading={true} spinnerSize={14} checkSize={14} />
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                            {isProcessing ? (
                              <div className="flex items-center justify-end gap-2">
                                <MiniLoadingToCheckmark isLoading={true} spinnerSize={16} checkSize={16} />
                                <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                  {processingMessage || t('Processing')}
                                </span>
                              </div>
                            ) : generatedDraft.status === 'completed' ? (
                              <Button
                                variant="primary"
                                size="small"
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewDraft(generatedDraft.document_type_id, generatedDraft.name); }}
                              >
                                {t('View')}
                              </Button>
                            ) : generatedDraft.status === 'failed' ? (
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); onGenerateDraft(tmpl.DraftType || tmpl.id, tmpl.id); }}
                                  disabled={!hasDocuments}
                                  tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                                >
                                  {t('Retry')}
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="primary"
                                size="small"
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onGenerateDraft(tmpl.DraftType || tmpl.id, tmpl.id); }}
                                disabled={!hasDocuments}
                                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                              >
                                {t('Use Template')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Orphaned generated drafts (template may have been deleted) */}
                    {orphanedGeneratedDrafts.map((draft) => {
                      const isGenerating = generatingDraftIds?.has(draft.document_type_id) || generatingDraftId === draft.document_type_id;
                      const isProcessing = isGenerating || draft.status === 'processing';
                      const { date, time } = (draft as any).generated_at
                        ? formatDateAndTime((draft as any).generated_at)
                        : { date: '', time: '' };

                      return (
                        <tr
                          key={draft.document_type_id}
                          className={clsx(
                            'transition-colors cursor-pointer',
                            isProcessing
                              ? 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                              : draft.status === 'completed'
                              ? 'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                          )}
                          onClick={() => onViewDraft(draft.document_type_id, draft.name)}
                        >
                          <td className="px-4 py-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {draft.name}
                            </p>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <Badge color="blue">{t('Template')}</Badge>
                          </td>
                          <td className="px-4 py-4 text-center">
                            {isProcessing ? (
                              <Badge color="blue" icon={<PiSpinnerGapLight className="h-3 w-3 animate-spin" />}>
                                {t('Processing')}
                              </Badge>
                            ) : draft.status === 'completed' ? (
                              <Badge color="green" icon={<PiCheckCircleLight className="h-3 w-3" />}>
                                {t('Completed')}
                              </Badge>
                            ) : draft.status === 'failed' ? (
                              <Badge color="red" icon={<PiWarningCircleLight className="h-3 w-3" />}>
                                {t('Failed')}
                              </Badge>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {(draft as any).generated_at ? (
                              <div>
                                <span className="text-sm text-gray-900 dark:text-white">{date}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{time}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                            {draft.status === 'completed' ? (
                              <Button
                                variant="primary"
                                size="small"
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewDraft(draft.document_type_id, draft.name); }}
                              >
                                {t('View')}
                              </Button>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Divider between generated and unused templates */}
                    {(templatesWithDrafts.length > 0 || orphanedGeneratedDrafts.length > 0) && templatesWithoutDrafts.length > 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-2">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">{t('Available Templates')}</span>
                            <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Unused templates */}
                    {templatesWithoutDrafts.map((tmpl) => {
                      const isGenerating = generatingDraftIds?.has(`template_${tmpl.id}`) || generatingDraftId === `template_${tmpl.id}`;

                      return (
                        <tr
                          key={tmpl.id}
                          className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                          onClick={() => history.push(`/templates/${tmpl.id}?type=structure`)}
                        >
                          <td className="px-4 py-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {tmpl.TemplateName}
                            </p>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <Badge color="gray">{tmpl.DraftType || '-'}</Badge>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {tmpl.Owner?.Initials && (
                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300">
                                  {tmpl.Owner.Initials}
                                </span>
                              )}
                              <span className="text-xs text-gray-600 dark:text-gray-400">{tmpl.Owner?.Name || tmpl.OwnerType || '-'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                            {isGenerating ? (
                              <div className="flex items-center justify-end gap-2">
                                <MiniLoadingToCheckmark isLoading={true} spinnerSize={16} checkSize={16} />
                                <span className="text-xs text-gray-600 dark:text-gray-400">{t('Generating')}</span>
                              </div>
                            ) : (
                              <Button
                                variant="primary"
                                size="small"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onGenerateDraft(tmpl.DraftType || tmpl.id, tmpl.id);
                                }}
                                disabled={!hasDocuments}
                                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                              >
                                {t('Use Template')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        );
      })()}

      {/* Upgrade-to-AI banner when showing rule-based suggestions */}
      {draftSubTab !== 'templates' && filteredDrafts.length === 0 && !loading && !(draftSubTab === 'suggested' && (isGeneratingSuggestions || suggestionsProgress?.status === 'complete' || suggestionsProgress?.status === 'failed')) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <PiFileTextLight className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            {t('No drafts found')}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 max-w-xs">
            {searchTerm
              ? t('Try adjusting your search terms')
              : draftSubTab === 'suggested'
                ? t('Generate AI-powered suggestions to see which drafts are most relevant for this matter')
                : t('Select a draft type to generate your first document')}
          </p>
          {draftSubTab === 'suggested' && !searchTerm && onGenerateSuggestions && (
            <Button
              size='small'
              variant='primary'
              onClick={onGenerateSuggestions}
              disabled={isGeneratingSuggestions || !hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={isGeneratingSuggestions
                ? <PiSpinnerGapLight className="h-3.5 w-3.5 animate-spin" />
                : <PiSparkleLight className="h-3.5 w-3.5" />
              }
              className='mt-4' 
            >
              {isGeneratingSuggestions ? t('Generating...') : t('Generate Suggestions')}
            </Button>
          )}
        </div>
      )}

      {draftSubTab !== 'templates' && viewMode === 'table' && filteredDrafts.length > 0 && !(draftSubTab === 'suggested' && (isGeneratingSuggestions || suggestionsProgress?.status === 'complete' || suggestionsProgress?.status === 'failed')) && (
        <div className="overflow-x-auto max-h-[calc(100vh-220px)] custom-scrollbar-main" style={{ userSelect: isResizing ? 'none' : 'auto' }}>
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              {(() => {
                const showPriority = draftSubTab === 'suggested' && aiSuggestionMap.size > 0;
                const cols = showPriority
                  ? ['Name', 'Priority', 'Category', 'Status', 'Generated', 'Versions', 'Actions']
                  : ['Name', 'Category', 'Status', 'Generated', 'Versions', 'Actions'];
                return (
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    {cols.map((col, idx) => {
                      const colWidth = columnWidths[col];
                      const isLastColumn = idx === cols.length - 1;

                      return (
                        <th
                          key={col}
                          className={clsx(
                            'relative px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300',
                            col === 'Actions' && 'text-right'
                          )}
                          style={colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px` } : undefined}
                        >
                          <div className="flex items-center justify-between h-full">
                            <span>{t(col)}</span>
                            {!isLastColumn && col !== 'Actions' && (
                              <div
                                onMouseDown={(e) => handleResizeStart(e, col)}
                                className={clsx(
                                  'table-resize-handle',
                                  isResizing && resizingColRef.current === col && 'resizing'
                                )}
                                title="Drag to resize column"
                              />
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })()}
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDrafts.map((draft) => {
                const { date, time } = (draft as any).generated_at
                  ? formatDateAndTime((draft as any).generated_at)
                  : { date: '', time: '' };
                const isExpanded = expandedVersions.has(draft.document_type_id);
                const isGenerated = draft.status === 'completed' && (draft as any).generated_at;
                const isProcessing = generatingDraftIds?.has(draft.document_type_id) || generatingDraftId === draft.document_type_id || draft.status === 'processing';

                return (
                  <React.Fragment key={draft.document_type_id}>
                    <tr
                      className={clsx(
                        'transition-colors cursor-pointer',
                        isProcessing
                          ? 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                          : isGenerated
                          ? 'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      )}
                      onClick={() => onViewDraft(draft.document_type_id, draft.name)}
                    >
                      <td className="px-4 py-4" style={{ width: `${columnWidths['Name']}px`, minWidth: `${columnWidths['Name']}px` }}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {draft.name}
                            </p>
                            {/* Show AI reason as subtitle when available; otherwise show description */}
                            {aiSuggestionMap.has(draft.document_type_id) ? (
                              <Tooltip
                                title={`${t('Recommended by PONS')}: ${aiSuggestionMap.get(draft.document_type_id)?.reason || ''}`}
                                arrow
                                placement="bottom-start"
                                enterDelay={300}
                                slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: '0.75rem', lineHeight: 1.5 } } }}
                              >
                                <div className="flex items-start gap-1 mt-0.5 cursor-help">
                                  <PiSparkleLight className="h-3.5 w-3.5 mt-px flex-shrink-0 text-blue-500 dark:text-blue-400" />
                                  <p className="text-xs text-blue-600 dark:text-blue-400 line-clamp-2">
                                    {aiSuggestionMap.get(draft.document_type_id)?.reason}
                                  </p>
                                </div>
                              </Tooltip>
                            ) : (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1" title={draft.description}>
                                {draft.description}
                              </p>
                            )}
                          </div>
                          {draft.isPonsCreated && (
                            <img
                              src="/favicon-adaptive.svg"
                              alt="PONS"
                              className="h-4 w-4 opacity-70 flex-shrink-0"
                              title="Created by PONS"
                            />
                          )}
                        </div>
                      </td>

                      {/* Priority column — only shown on Suggested sub-tab */}
                      {draftSubTab === 'suggested' && aiSuggestionMap.size > 0 && (
                        <td className="px-4 py-4" style={{ width: `${columnWidths['Priority']}px`, minWidth: `${columnWidths['Priority']}px` }}>
                          {aiSuggestionMap.has(draft.document_type_id) && (() => {
                            const priority = aiSuggestionMap.get(draft.document_type_id)?.priority;
                            const priorityConfig = {
                              high: { icon: <PiArrowFatUpLight className="h-3 w-3" />, color: 'red' as const, label: t('High') },
                              medium: { icon: <PiMinusLight className="h-3 w-3" />, color: 'yellow' as const, label: t('Medium') },
                              low: { icon: <PiArrowFatDownLight className="h-3 w-3" />, color: 'blue' as const, label: t('Low') },
                            };
                            const c = priorityConfig[priority || 'low'];
                            return (
                              <Badge color={c.color} icon={c.icon}>
                                {c.label}
                              </Badge>
                            );
                          })()}
                        </td>
                      )}

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Category']}px`, minWidth: `${columnWidths['Category']}px` }}>
                        {(draft as any).isTemplateGenerated ? (
                          <Badge color="blue">{t('Template')}</Badge>
                        ) : (
                          <Badge color="gray">{formatCategoryName(draft.category)}</Badge>
                        )}
                      </td>

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Status']}px`, minWidth: `${columnWidths['Status']}px` }}>
                        {draft.finalized ? (
                          <Badge
                            color="green"
                            icon={<PiCheckCircleLight className="h-3 w-3" />}
                            tooltip={draft.finalized_at ? `Finalized on ${formatDateTime(draft.finalized_at)}` : undefined}
                          >
                            {t('Finalized')}
                          </Badge>
                        ) : isProcessing ? (
                          <Badge color="blue" icon={<PiSpinnerGapLight className="h-3 w-3 animate-spin" />}>
                            {t('Processing')}
                          </Badge>
                        ) : draft.status === 'completed' ? (
                          <Badge color="green">
                            {t('Completed')}
                          </Badge>
                        ) : draft.status === 'failed' ? (
                          <Badge color="red" icon={<PiWarningCircleLight className="h-3 w-3" />}>
                            {t('Failed')}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Generated']}px`, minWidth: `${columnWidths['Generated']}px` }}>
                        {(draft as any).generated_at ? (
                          <div>
                            <span className="text-sm text-gray-900 dark:text-white">{date}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{time}</span>
                          </div>
                        ) : draft.status === 'processing' ? (
                          <Badge color="blue" icon={<MiniLoadingToCheckmark isLoading={true} spinnerSize={12} checkSize={12} />}>
                            {t('Processing')}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Versions']}px`, minWidth: `${columnWidths['Versions']}px` }} onClick={(e) => e.stopPropagation()}>
                        {draft.allVersions.length > 0 ? (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleVersions(draft.document_type_id);
                            }}
                            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline focus:outline-none"
                          >
                            {draft.allVersions.length}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>

                      <td className="px-4 py-4 text-right" style={{ width: `${columnWidths['Actions']}px`, minWidth: `${columnWidths['Actions']}px` }} onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">{renderActionButtons(draft)}</div>
                      </td>
                    </tr>

                    {isExpanded && draft.allVersions.length > 0 && (
                      <tr className="bg-gray-50 dark:bg-gray-800/30">
                        <td colSpan={draftSubTab === 'suggested' && aiSuggestionMap.size > 0 ? 7 : 6} className="px-4 py-4">
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                              Version History
                            </p>
                            {draft.allVersions.map((version, idx) => {
                              // Use modified date if available, otherwise generated_at
                              const dateField = (version as any).modified || version.generated_at;
                              const versionDate = formatDateAndTime(dateField);
                              const versionId = (version as any).version_id;
                              const isFinalizedVersion = draft.finalized && idx === 0; // First version is the finalized one
                              const canSelectVersion = !draft.finalized || isFinalizedVersion;
                              return (
                                <button
                                  key={`${draft.document_type_id}-v${version.version}-${versionId || idx}`}
                                  onClick={() => canSelectVersion && onViewDraft(draft.document_type_id, draft.name, versionId)}
                                  disabled={!canSelectVersion}
                                  title={!canSelectVersion ? 'Cannot view older versions of a finalized draft' : undefined}
                                  className={`w-full flex items-center justify-between p-3 bg-white dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600 transition-all ${
                                    canSelectVersion
                                      ? 'hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm cursor-pointer'
                                      : 'opacity-50 cursor-not-allowed'
                                  }`}
                                >
                                  <div className="flex-1 text-left">
                                    <div className="flex items-center gap-3">
                                      <Badge color="gray">v{version.version || idx + 1}</Badge>
                                      <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {versionDate.date} at {versionDate.time}
                                      </span>
                                      {isFinalizedVersion && (
                                        <Badge color="green">
                                          <span className="text-xs flex items-center gap-1">
                                            <PiCheckCircleLight className="h-2.5 w-2.5" />
                                            Final
                                          </span>
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  {version.status && <div>{renderStatusBadge(version.status)}</div>}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {draftSubTab !== 'templates' && viewMode === 'grid' && filteredDrafts.length > 0 && !(draftSubTab === 'suggested' && (isGeneratingSuggestions || suggestionsProgress?.status === 'complete' || suggestionsProgress?.status === 'failed')) && (
        <div className="p-6 max-h-[calc(100vh-220px)] overflow-y-auto custom-scrollbar-main">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDrafts.map((draft) => {
              const isGenerating = generatingDraftIds?.has(draft.document_type_id) || generatingDraftId === draft.document_type_id;
              const { date, time } = (draft as any).generated_at
                ? formatDateAndTime((draft as any).generated_at)
                : { date: '', time: '' };

              return (
                <div
                  key={draft.document_type_id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md dark:hover:shadow-dark transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      {aiSuggestionMap.has(draft.document_type_id) && (
                        <span
                          className={clsx(
                            'mt-1.5 h-2 w-2 rounded-full flex-shrink-0',
                            aiSuggestionMap.get(draft.document_type_id)?.priority === 'high'
                              ? 'bg-red-500'
                              : aiSuggestionMap.get(draft.document_type_id)?.priority === 'medium'
                              ? 'bg-amber-500'
                              : 'bg-blue-400'
                          )}
                          title={`${aiSuggestionMap.get(draft.document_type_id)?.priority} priority`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {draft.name}
                        </h4>
                        {aiSuggestionMap.has(draft.document_type_id) ? (
                          <Tooltip
                            title={`${t('Recommended by PONS')}: ${aiSuggestionMap.get(draft.document_type_id)?.reason || ''}`}
                            arrow
                            placement="bottom-start"
                            enterDelay={300}
                            slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: '0.75rem', lineHeight: 1.5 } } }}
                          >
                            <div className="flex items-start gap-1 mt-0.5 cursor-help">
                              <PiSparkleLight className="h-3.5 w-3.5 mt-px flex-shrink-0 text-blue-500 dark:text-blue-400" />
                              <p className="text-xs text-blue-600 dark:text-blue-400 line-clamp-2">
                                {aiSuggestionMap.get(draft.document_type_id)?.reason}
                              </p>
                            </div>
                          </Tooltip>
                        ) : (
                          <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                            {draft.description}
                          </p>
                        )}
                      </div>
                    </div>
                    {draft.isPonsCreated && (
                      <img
                        src="/favicon-adaptive.svg"
                        alt="PONS"
                        className="h-4 w-4 opacity-70 flex-shrink-0"
                      />
                    )}
                  </div>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Badge color="gray">{formatCategoryName(draft.category)}</Badge>
                      {draft.finalized ? (
                        <Badge
                          color="green"
                          icon={<PiCheckCircleLight className="h-3 w-3" />}
                          tooltip={draft.finalized_at ? `Finalized on ${formatDateTime(draft.finalized_at)}` : undefined}
                        >
                          {t('Finalized')}
                        </Badge>
                      ) : (
                        <Badge color="gray">{t('Not Finalized')}</Badge>
                      )}
                    </div>

                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {(draft as any).generated_at ? (
                        <>
                          <p>Generated: {date} {time}</p>
                          <p>Versions: {draft.allVersions.length}</p>
                        </>
                      ) : draft.status === 'processing' ? (
                        <p>{t('Processing...')}</p>
                      ) : (
                        <p>{t('Not Generated')}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">{renderActionButtons(draft)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedDraftsView;
